/**
 * Track Readiness Evaluator (v3.0 - Track-Authoritative + RPC)
 * 
 * Per spec: "Each Track Overview must display readiness panels for S1-S6"
 * 
 * This evaluator checks readiness CONDITIONS for each decision class:
 * - S1: Participation Locked
 * - S2: Item Pool Locked  
 * - S3: Criteria Defined
 * - S4: Value Scales Locked
 * - S5: Values Converged
 * - S6: Tool Compatible (requires S1-S5)
 * 
 * S6 is blocked if ANY prerequisite S1-S5 condition is false.
 * 
 * These are CONDITIONS, not ordered steps. The user may complete them
 * in any order, but S6 requires all of them.
 * 
 * v3.0 Changes:
 * - Prefers RPC `evaluate_track_readiness` for server-authoritative evaluation
 * - Falls back to local evaluation if RPC unavailable
 * - Uses Track-native lock states instead of egap_iterations
 *
 * S1 (participation) readiness: Based on EFFECTIVE track participants only:
 * - cycle_participants + cycle_participant_groups + track_participants + track_participant_groups.
 * Not cycle_participants alone. Same as RPC (migrations 097/168).
 */

import { getSafeSupabaseClient } from './supabaseSync';
import { trackToTrackCell, type TrackCell } from '../types/prioritization/track';
import { resolveEffectiveParticipantsForTrack } from './effectiveParticipantResolver';
import { getMemberships } from './groupsService';

// ============================================================================
// Types
// ============================================================================

export type ReadinessLevel = 'ready' | 'warning' | 'blocked';

export interface ReadinessCondition {
  ready: boolean;
  level: ReadinessLevel;
  blockers: string[];
  warnings: string[];
  actions: string[];
}

export interface TrackReadinessStatus {
  trackId: string;
  trackName: string;
  cycleId: string;
  /** Semantic core: the comparability lane (item_category × altitude_level). */
  trackCell: TrackCell;

  s1_participation: ReadinessCondition;
  s2_pool: ReadinessCondition;
  s3_criteria: ReadinessCondition;
  s4_scales: ReadinessCondition;
  s5_values: ReadinessCondition;
  s6_tool: ReadinessCondition;
  
  allPrerequisitesMet: boolean;
  s6Ready: boolean;
  
  summary: {
    readyCount: number;
    warningCount: number;
    blockedCount: number;
  };

  /**
   * Which evaluator produced this: 'server' (evaluate_track_readiness, the
   * authority) or 'local' (the in-browser fallback). A fallback is only
   * silent when the RPC does not exist in this environment; any other server
   * failure is carried in `serverError` so the caller can say so.
   */
  source?: 'server' | 'local';
  serverError?: string;
}

interface TrackData {
  id: string;
  name: string;
  cycleId: string;
  status: string;
}

interface IterationData {
  id: string;
  participationLockedAt: string | null;
  itemPoolLockedAt: string | null;
  criteriaLockedAt: string | null;
  valuesConvergedAt: string | null;
}

interface PoolData {
  id: string;
  priorityIds: string[] | null;
  isLocked: boolean;
}

interface CriteriaConfigData {
  id: string;
  attributeKey: string;
  rangeStatus: string | null;
}

// ============================================================================
// Evaluator
// ============================================================================

/**
 * Flatten the blockers out of a server readiness payload (the JSON that
 * `evaluate_track_readiness` returns and `run_session` echoes back on a
 * refusal): `{ s1_participation: { ready, blockers: [] }, … }`.
 * Tolerant of missing / malformed input — returns [] rather than throwing.
 */
export function collectReadinessBlockers(readiness: unknown): string[] {
  if (!readiness || typeof readiness !== 'object') return [];
  const out: string[] = [];
  for (const value of Object.values(readiness as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const condition = value as { ready?: unknown; blockers?: unknown };
    if (condition.ready === true || !Array.isArray(condition.blockers)) continue;
    for (const blocker of condition.blockers) {
      if (typeof blocker === 'string' && blocker.trim() && !out.includes(blocker)) out.push(blocker);
    }
  }
  return out;
}

/**
 * Evaluate readiness for all S1-S6 conditions for a specific track.
 * 
 * Per spec: "Each panel should show status, required actions, and a link 
 * to the relevant screen. S6 must be blocked if any prerequisite S1-S5 
 * condition is false."
 * 
 * Prefers the server-side RPC `evaluate_track_readiness` when available.
 * Falls back to local evaluation if RPC unavailable.
 */
export async function evaluateTrackReadiness(trackId: string): Promise<TrackReadinessStatus | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    console.error('[TrackReadinessEvaluator] Supabase not available');
    return null;
  }

  // Try RPC first (server-authoritative)
  const rpc = await tryRpcEvaluation(supabase, trackId);
  if (rpc.status) {
    return { ...rpc.status, source: 'server' };
  }

  // Fall back to local evaluation — and say why when it was not simply a
  // missing RPC, so a server refusal/error is never silently replaced.
  const local = await evaluateTrackReadinessLocal(supabase, trackId);
  if (!local) return null;
  return rpc.serverError
    ? { ...local, source: 'local', serverError: rpc.serverError }
    : { ...local, source: 'local' };
}

/**
 * Try to use the server-side RPC for evaluation.
 * Returns null if RPC doesn't exist or fails.
 * RPC evaluate_track_readiness (migrations 097/168) uses the same S1 model as local:
 * effective track participants (cycle_participants + cycle_participant_groups + track_participants + track_participant_groups).
 */
export async function tryRpcEvaluation(
  supabase: ReturnType<typeof getSafeSupabaseClient>,
  trackId: string
): Promise<{ status: TrackReadinessStatus | null; serverError?: string }> {
  if (!supabase) return { status: null };

  try {
    const { data, error } = await supabase.rpc('evaluate_track_readiness', {
      p_track_id: trackId
    });

    if (error) {
      // Only a missing function is a quiet fallback (older environment).
      if (error.code === 'PGRST202' || error.message.includes('Could not find the function')) {
        console.debug('[TrackReadinessEvaluator] RPC not available, using local evaluation');
        return { status: null };
      }
      console.warn('[TrackReadinessEvaluator] RPC error:', error.message);
      return { status: null, serverError: error.message };
    }

    if (!data || data.error) {
      console.warn('[TrackReadinessEvaluator] RPC returned error:', data?.error);
      return { status: null, serverError: String(data?.error ?? 'The server returned no readiness result.') };
    }

    // Convert RPC result to TrackReadinessStatus
    return { status: convertRpcResult(data) };
  } catch (err) {
    console.warn('[TrackReadinessEvaluator] RPC call failed:', err);
    return { status: null, serverError: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Convert RPC JSONB result to TrackReadinessStatus type
 */
function convertRpcResult(data: Record<string, unknown>): TrackReadinessStatus {
  const convertCondition = (rpcCondition: Record<string, unknown>): ReadinessCondition => {
    const ready = Boolean(rpcCondition.ready);
    const blockers = (rpcCondition.blockers as string[]) || [];
    return {
      ready,
      level: blockers.length > 0 ? 'blocked' : ready ? 'ready' : 'warning',
      blockers,
      warnings: [],
      actions: blockers.length > 0 ? ['Complete this step'] : [],
    };
  };

  const s1 = convertCondition(data.s1_participation as Record<string, unknown>);
  const s2 = convertCondition(data.s2_pool as Record<string, unknown>);
  const s3 = convertCondition(data.s3_criteria as Record<string, unknown>);
  const s4 = convertCondition(data.s4_scales as Record<string, unknown>);
  const s5 = convertCondition(data.s5_values as Record<string, unknown>);
  const s6 = convertCondition(data.s6_tool as Record<string, unknown>);

  const conditions = [s1, s2, s3, s4, s5, s6];

  return {
    trackId: data.track_id as string,
    trackName: data.track_name as string,
    cycleId: data.cycle_id as string,
    trackCell: {
      itemCategory: '', // RPC doesn't return track cell details
      altitudeLevel: 0,
    },
    s1_participation: s1,
    s2_pool: s2,
    s3_criteria: s3,
    s4_scales: s4,
    s5_values: s5,
    s6_tool: s6,
    allPrerequisitesMet: Boolean(data.all_prerequisites_met),
    s6Ready: Boolean(data.s6_ready),
    summary: {
      readyCount: conditions.filter(c => c.level === 'ready').length,
      warningCount: conditions.filter(c => c.level === 'warning').length,
      blockedCount: conditions.filter(c => c.level === 'blocked').length,
    },
  };
}

/**
 * Local evaluation fallback (used when RPC not available).
 * Uses Track-native lock states from tracks table.
 */
async function evaluateTrackReadinessLocal(
  supabase: NonNullable<ReturnType<typeof getSafeSupabaseClient>>,
  trackId: string
): Promise<TrackReadinessStatus | null> {
  // Fetch track data with Track-native lock states
  const { data: track, error: trackError } = await supabase
    .from('tracks')
    .select('id, name, cycle_id, status, required_item_category, required_altitude_level, participation_locked_at, session_baselines(criteria_locked_at, values_converged_at, pool_locked_at)')
    .eq('id', trackId)
    .single();

  if (trackError || !track) {
    console.error('[TrackReadinessEvaluator] Track not found:', trackId, trackError);
    return null;
  }

  const trackData: TrackData = {
    id: track.id,
    name: track.name,
    cycleId: track.cycle_id,
    status: track.status,
  };

  const trackCell = trackToTrackCell({
    requiredItemCategory: track.required_item_category,
    requiredAltitudeLevel: track.required_altitude_level,
  });

  // Build iteration data from lock states — the Session Baseline is the sole
  // record (canon 28 Phase C-1); the mirrored tracks columns were dropped.
  const rawBaseline = (track as unknown as { session_baselines?: unknown }).session_baselines;
  const baseline = (Array.isArray(rawBaseline) ? rawBaseline[0] : rawBaseline) as
    | { criteria_locked_at?: string | null; values_converged_at?: string | null; pool_locked_at?: string | null }
    | undefined;
  const trackLockStates: IterationData = {
    id: track.id,
    participationLockedAt: track.participation_locked_at,
    itemPoolLockedAt: baseline?.pool_locked_at ?? null,
    criteriaLockedAt: baseline?.criteria_locked_at ?? null,
    valuesConvergedAt: baseline?.values_converged_at ?? null,
  };

  // Fetch item pools for this Track Instance
  const { data: pools } = await supabase
    .from('item_pools')
    .select('id, priority_ids, pool_locked_at')
    .eq('track_id', trackId);

  const poolData: PoolData[] = (pools || []).map((p: { id: string; priority_ids?: string[] | null; pool_locked_at?: string | null }) => ({
    id: p.id,
    priorityIds: p.priority_ids ?? null,
    isLocked: !!p.pool_locked_at,
  }));

  // Fetch criteria configs for this Track Instance
  const { data: criteriaConfigs } = await supabase
    .from('cycle_attribute_configs')
    .select('id, attribute_key, range_status')
    .eq('track_id', trackId);

  const criteriaData: CriteriaConfigData[] = (criteriaConfigs || []).map(c => ({
    id: c.id,
    attributeKey: c.attribute_key,
    rangeStatus: c.range_status,
  }));

  // S1: Effective track participants (cycle direct + cycle groups + track direct + track groups). Do not use cycle_participants alone.
  const safeQuery = async <T>(table: string, select: string, filters: Record<string, unknown>): Promise<T[]> => {
    let q = supabase.from(table).select(select);
    for (const [k, v] of Object.entries(filters)) {
      if (Array.isArray(v)) q = q.in(k, v);
      else if (v != null) q = q.eq(k, v);
    }
    const { data, error } = await q;
    if (error && (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist'))) return [];
    if (error) throw error;
    return (data || []) as T[];
  };

  const cycleParticipants = await safeQuery<{ user_id: string }>('cycle_participants', 'user_id', {
    cycle_id: trackData.cycleId,
    status: ['active', 'invited'],
  });
  const cycleGroups = await safeQuery<{ group_id: string }>('cycle_participant_groups', 'group_id', {
    cycle_id: trackData.cycleId,
    status: 'active',
  });
  const trackParticipants = await safeQuery<{ user_id: string }>('track_participants', 'user_id', {
    track_id: trackId,
    status: 'active',
  });
  const trackGroups = await safeQuery<{ group_id: string }>('track_participant_groups', 'group_id', {
    track_id: trackId,
    status: 'active',
  });

  const cycleDirectUsers = cycleParticipants.map(r => ({ userId: r.user_id }));
  const cycleParticipantGroups = cycleGroups.map(r => ({ groupId: r.group_id }));
  const trackDirectUsers = trackParticipants.map(r => ({ userId: r.user_id }));
  const trackParticipantGroups = trackGroups.map(r => ({ groupId: r.group_id }));

  let activeParticipantCount = 0;
  try {
    const effective = await resolveEffectiveParticipantsForTrack({
      cycleDirectUsers,
      cycleParticipantGroups,
      trackDirectUsers,
      trackParticipantGroups,
      getMemberUserIdsForGroup: async (groupId: string) => {
        const memberships = await getMemberships({ groupId });
        return [...new Set(memberships.map(m => m.userId).filter(Boolean))];
      },
    });
    activeParticipantCount = effective.length;
  } catch (err) {
    console.warn('[TrackReadinessEvaluator] Effective participant resolution failed, using cycle direct only:', err);
    activeParticipantCount = cycleDirectUsers.length;
  }

  // Fetch value proposals for this track
  const { data: valueProposals } = await supabase
    .from('value_proposals')
    .select('id, status')
    .eq('track_id', trackId)
    .eq('status', 'accepted');

  const acceptedValueCount = valueProposals?.length || 0;

  // Evaluate each condition using Track-native lock states
  const s1 = evaluateS1Participation(trackLockStates, activeParticipantCount);
  const s2 = evaluateS2Pool(trackLockStates, poolData);
  const s3 = evaluateS3Criteria(trackLockStates, criteriaData);
  const s4 = evaluateS4Scales(trackLockStates, criteriaData);
  const s5 = evaluateS5Values(trackLockStates, acceptedValueCount, criteriaData.length, poolData);
  
  const prerequisitesMet = s1.ready && s2.ready && s3.ready && s4.ready && s5.ready;
  const s6 = evaluateS6Tool(prerequisitesMet, s1, s2, s3, s4, s5);

  const conditions = [s1, s2, s3, s4, s5, s6];
  const summary = {
    readyCount: conditions.filter(c => c.level === 'ready').length,
    warningCount: conditions.filter(c => c.level === 'warning').length,
    blockedCount: conditions.filter(c => c.level === 'blocked').length,
  };

  return {
    trackId: trackData.id,
    trackName: trackData.name,
    cycleId: trackData.cycleId,
    trackCell,
    s1_participation: s1,
    s2_pool: s2,
    s3_criteria: s3,
    s4_scales: s4,
    s5_values: s5,
    s6_tool: s6,
    allPrerequisitesMet: prerequisitesMet,
    s6Ready: s6.ready,
    summary,
  };
}

// ============================================================================
// Individual Condition Evaluators
// ============================================================================

function evaluateS1Participation(
  iteration: IterationData | null,
  activeParticipantCount: number
): ReadinessCondition {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const actions: string[] = [];

  if (activeParticipantCount === 0) {
    blockers.push('No active participants');
    actions.push('Add at least one participant to the cycle');
  }

  if (!iteration?.participationLockedAt) {
    if (activeParticipantCount > 0) {
      warnings.push('Participation not locked');
      actions.push('Lock participation to prevent changes during prioritization');
    } else {
      blockers.push('Participation not locked');
      actions.push('Add participants and lock participation');
    }
  }

  const ready = blockers.length === 0 && !!iteration?.participationLockedAt;
  const level: ReadinessLevel = blockers.length > 0 ? 'blocked' : 
    warnings.length > 0 ? 'warning' : 'ready';

  return { ready, level, blockers, warnings, actions };
}

function evaluateS2Pool(
  iteration: IterationData | null,
  pools: PoolData[]
): ReadinessCondition {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const actions: string[] = [];

  const totalItems = pools.reduce((sum, p) => sum + (p.priorityIds?.length || 0), 0);
  const hasLockedPool = pools.some(p => p.isLocked);

  if (totalItems === 0) {
    blockers.push('No items in pool');
    actions.push('Add items to the track pool');
  }

  if (!iteration?.itemPoolLockedAt && !hasLockedPool) {
    if (totalItems > 0) {
      warnings.push('Item pool not locked');
      actions.push('Lock the item pool to prevent changes during prioritization');
    } else {
      blockers.push('Item pool not locked');
      actions.push('Add items and lock the pool');
    }
  }

  const ready = blockers.length === 0 && (!!iteration?.itemPoolLockedAt || hasLockedPool);
  const level: ReadinessLevel = blockers.length > 0 ? 'blocked' : 
    warnings.length > 0 ? 'warning' : 'ready';

  return { ready, level, blockers, warnings, actions };
}

function evaluateS3Criteria(
  iteration: IterationData | null,
  criteriaConfigs: CriteriaConfigData[]
): ReadinessCondition {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const actions: string[] = [];

  if (criteriaConfigs.length === 0) {
    blockers.push('No criteria defined');
    actions.push('Select at least one attribute as evaluation criteria');
  }

  if (!iteration?.criteriaLockedAt && criteriaConfigs.length > 0) {
    warnings.push('Criteria not locked');
    actions.push('Lock criteria to prevent changes during prioritization');
  }

  const ready = blockers.length === 0 && !!iteration?.criteriaLockedAt;
  const level: ReadinessLevel = blockers.length > 0 ? 'blocked' : 
    warnings.length > 0 ? 'warning' : 'ready';

  return { ready, level, blockers, warnings, actions };
}

function evaluateS4Scales(
  _iteration: IterationData | null,
  criteriaConfigs: CriteriaConfigData[]
): ReadinessCondition {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const actions: string[] = [];

  if (criteriaConfigs.length === 0) {
    blockers.push('No criteria to configure scales for');
    actions.push('Define criteria first (S3)');
  } else {
    const acceptedScales = criteriaConfigs.filter(c => c.rangeStatus === 'accepted');
    const pendingScales = criteriaConfigs.filter(c => c.rangeStatus !== 'accepted');

    if (pendingScales.length > 0) {
      blockers.push(`${pendingScales.length} scale(s) not accepted`);
      actions.push('Accept value scales for all criteria');
    }

    if (acceptedScales.length > 0 && acceptedScales.length < criteriaConfigs.length) {
      warnings.push(`${acceptedScales.length}/${criteriaConfigs.length} scales ready`);
    }
  }

  const allScalesAccepted = criteriaConfigs.length > 0 && 
    criteriaConfigs.every(c => c.rangeStatus === 'accepted');

  const ready = allScalesAccepted;
  const level: ReadinessLevel = blockers.length > 0 ? 'blocked' : 
    warnings.length > 0 ? 'warning' : 'ready';

  return { ready, level, blockers, warnings, actions };
}

function evaluateS5Values(
  iteration: IterationData | null,
  acceptedValueCount: number,
  criteriaCount: number,
  pools: PoolData[]
): ReadinessCondition {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const actions: string[] = [];

  // When the track is marked "values converged", S5 is ready (authoritative flag).
  // This keeps the Overview in sync with the header checkbox.
  if (iteration?.valuesConvergedAt) {
    return {
      ready: true,
      level: 'ready',
      blockers: [],
      warnings: [],
      actions: ['View'],
    };
  }

  const totalItems = pools.reduce((sum, p) => sum + (p.priorityIds?.length || 0), 0);
  const expectedValues = totalItems * criteriaCount;

  if (criteriaCount === 0) {
    blockers.push('No criteria defined for value entry');
    actions.push('Define criteria first (S3/S4)');
  } else if (totalItems === 0) {
    blockers.push('No items in pool for value entry');
    actions.push('Add items to pool first (S2)');
  } else if (acceptedValueCount === 0) {
    blockers.push('No values entered');
    actions.push('Enter values for items against each criterion');
  } else if (acceptedValueCount < expectedValues) {
    warnings.push(`${acceptedValueCount}/${expectedValues} values entered`);
    actions.push('Complete value entry for all item/criteria combinations');
  }

  if (acceptedValueCount > 0) {
    warnings.push('Values not converged');
    actions.push('Run value convergence to finalize values');
  }

  const ready = false; // already returned above when valuesConvergedAt set
  const level: ReadinessLevel = blockers.length > 0 ? 'blocked' : 
    warnings.length > 0 ? 'warning' : 'ready';

  return { ready, level, blockers, warnings, actions };
}

function evaluateS6Tool(
  prerequisitesMet: boolean,
  s1: ReadinessCondition,
  s2: ReadinessCondition,
  s3: ReadinessCondition,
  s4: ReadinessCondition,
  s5: ReadinessCondition
): ReadinessCondition {
  const blockers: string[] = [];
  const warnings: string[] = [];
  const actions: string[] = [];

  if (!prerequisitesMet) {
    blockers.push('Prerequisites not met');
    
    if (!s1.ready) {
      blockers.push('Participation incomplete');
      actions.push('Complete participation setup');
    }
    if (!s2.ready) {
      blockers.push('Item pool incomplete');
      actions.push('Complete item pool setup');
    }
    if (!s3.ready) {
      blockers.push('Criteria incomplete');
      actions.push('Complete criteria setup');
    }
    if (!s4.ready) {
      blockers.push('Scales incomplete');
      actions.push('Complete scale setup');
    }
    if (!s5.ready) {
      blockers.push('Values incomplete');
      actions.push('Complete value entry');
    }
  }

  const ready = prerequisitesMet;
  const level: ReadinessLevel = blockers.length > 0 ? 'blocked' : 'ready';

  return { ready, level, blockers, warnings, actions };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get a summary string for track readiness (conditions-met language).
 */
export function getReadinessSummary(status: TrackReadinessStatus): string {
  if (status.s6Ready) {
    return 'Ranking ready';
  }

  const { blockedCount, warningCount } = status.summary;
  if (blockedCount > 0) {
    return `${blockedCount} condition(s) not met`;
  }
  if (warningCount > 0) {
    return `${warningCount} condition(s) have warnings`;
  }

  return 'Checking conditions...';
}

const CONDITION_KEYS: (keyof Pick<TrackReadinessStatus, 's1_participation' | 's2_pool' | 's3_criteria' | 's4_scales' | 's5_values' | 's6_tool'>)[] = [
  's1_participation',
  's2_pool',
  's3_criteria',
  's4_scales',
  's5_values',
  's6_tool',
];

const CONDITION_STEP_LABELS: Record<(typeof CONDITION_KEYS)[number], string> = {
  s1_participation: 'Participation',
  s2_pool: 'Item pool',
  s3_criteria: 'Criteria',
  s4_scales: 'Value scales',
  s5_values: 'Values',
  s6_tool: 'Ranking',
};

/**
 * Get a recommended next action (advisory only; user can enter any area).
 */
export function getNextAction(status: TrackReadinessStatus): { step: string; action: string } | null {
  for (const key of CONDITION_KEYS) {
    const condition = status[key];
    if (!condition.ready && condition.actions.length > 0) {
      return { step: CONDITION_STEP_LABELS[key], action: condition.actions[0] };
    }
  }
  return null;
}
